import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * The header's back arrow on a drill-down screen: back to where the screen was
 * opened from, as the back gesture does — pushing the parent would leave this
 * screen one back away. On the first entry of the app's history (a shared
 * link, a redirect), there is nothing in the app to go back to: it replaces
 * the screen with `fallbackPath` instead of leaving the app.
 */
export function useBackOr(fallbackPath: string): () => void {
  const navigate = useNavigate();
  const { key } = useLocation();

  return useCallback(() => {
    // The router's index in history: 0 on its first entry, whichever way the
    // app got there. "default" is the key of a cold load's first entry.
    const state: unknown = window.history.state;
    const index =
      state && typeof state === "object" && "idx" in state
        ? (state as { idx: unknown }).idx
        : null;
    // An entry without it (a state replaced by hand) is read as the first:
    // landing on the fallback beats leaving the app.
    if (key === "default" || typeof index !== "number" || index === 0) {
      navigate(fallbackPath, { replace: true });
    } else {
      navigate(-1);
    }
  }, [fallbackPath, key, navigate]);
}
